import React, { useEffect } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { RootState, useAppDispatch, useAppSelector } from '../../store/store';
import { handleToggleChangeRoleDialog, changeRole } from '../../store/usermanagement-slice/usermanagement-slice';
import { Header } from '../common/Header';
import { handleNotificationVariant } from '../notifications/_common';
import { enqueueSnackbar } from '../../store/app-slice/app';
import { MLRoles } from '../layout/menu-items';
import Transition from '../common/Transition';

const changeRoleSchema = (t: Function) => yup.object().shape({
  role: yup.string().required(t('USER_MANAGEMENT_ROLE_VALIDATION'))
});

const defaultValues = { role: '' };

export const ChangeRoleDialog = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { changeRoleDialog, changeRoleBodyParams } = useAppSelector((state: RootState) => state.usermanagement);
  const { control, handleSubmit, formState: { errors }, reset } = useForm({
    mode: 'onBlur',
    resolver: yupResolver(changeRoleSchema(t)),
    defaultValues
  });

  function handleClose() {
    reset(defaultValues);
    dispatch(handleToggleChangeRoleDialog({ open: false, body: null }));
  }

  function handleChangeRole(data: any) {
    dispatch(changeRole({ ...changeRoleBodyParams, newRole: data.role })).then((res: any) => {
      if (res && res.payload) {
        dispatch(enqueueSnackbar({
          message: t(`${res.payload.message}`),
          options: {
            variant: handleNotificationVariant(res.payload.success),
            persist: !res.payload.success
          }
        }));
      }
    });
  }

  useEffect(() => {
    if (!changeRoleDialog) {
      reset(defaultValues);
    }
  }, [changeRoleDialog, reset]);

  return (
    <Dialog
      TransitionComponent={Transition}
      open={changeRoleDialog}
      onClose={handleClose}
    >
      <form onSubmit={handleSubmit(handleChangeRole)}>
        <Header title={t('USER_MANAGEMENT_CHANGE_ROLE')} />
        <DialogContent>
          <DialogContentText>
            {t('USER_MANAGEMENT_CHANGE_ROLE_USER', { email: changeRoleBodyParams?.email })}
          </DialogContentText>
          <br />
          <Controller control={control} name="role" render={({ field, fieldState: { error } }) => (
            <FormControl fullWidth error={error !== undefined}>
              <InputLabel id="role-label">{t('USER_MANAGEMENT_ROLE_LABEL')}</InputLabel>
              <Select
                labelId="role-label"
                label={t('USER_MANAGEMENT_ROLE_LABEL')}
                fullWidth
                variant="outlined"
                {...field}
              >
                {Object.values(MLRoles)?.map((value) => (
                  <MenuItem key={value} value={value}>{value}</MenuItem>
                ))}
              </Select>
              {errors ? <FormHelperText>{errors.role?.message}</FormHelperText> : ''}
            </FormControl>
          )}
          />
        </DialogContent>
        <DialogActions>
          <Button className="button-text-color" onClick={handleClose}>{t('CANCEL_BUTTON')}</Button>
          <Button className="button-text-color" type="submit">{t('SUBMIT_BUTTON')}</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
